import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import BootstrapTheme from "@fullcalendar/bootstrap";
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';
import { usePocketBase } from '../../PocketBaseContext';

import CreateEventModal from './CreateEventModal';
import EditEventModal from './EditEventModal';
import DraggableEvents from './DraggableEvents';
import UpcomingEvents from './UpcomingEvents';
import { getEventClass } from './CalendarUtils';
import { Draggable } from '@fullcalendar/interaction';

const MonthGrid = () => {
  const pb = usePocketBase();
  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Refresh için state eklendi

  const fetchEvents = useCallback(async () => {
    try {
      const records = await pb.collection('events').getFullList({
        sort: 'start_date',
        expand: 'customer_id,service_id,product_id,invoice_id',
      });

      const formattedEvents = records.map(record => ({
        id: record.id,
        title: record.title,
        start: record.start_date,
        end: record.end_date,
        className: getEventClass(record.event_type[0]),
        location: record.location,
        description: record.description,
        extendedProps: {
          customer_ids: record.customer_id || [],
          service_ids: record.service_id || [],
          product_ids: record.product_id || [],
          invoice_ids: record.invoice_id || [],
          location: record.location,
          description: record.description,
          status: record.status,
          event_type: record.event_type || [],
        }
      }));

      setEvents(formattedEvents);

      const now = new Date();
      const upcomingEvts = formattedEvents
        .filter(event => new Date(event.start) > now)
        .sort((a, b) => new Date(a.start) - new Date(b.start))
        .slice(0, 5);
      setUpcomingEvents(upcomingEvts);

    } catch (error) {
      console.error("Error fetching events:", error);
    }
  }, [pb]);

  // Refresh key değiştiğinde verileri yeniden yükle
  useEffect(() => {
    fetchEvents();
    new Draggable(document.getElementById("external-events"), {
      itemSelector: ".external-event"
    });
  }, [fetchEvents, refreshKey]);

  const handleDateClick = (arg) => {
    const clickedDate = arg.date;
    setSelectedEvent({
      start: clickedDate,
      end: clickedDate
    });
    setIsCreateModalOpen(true);
  };

  const handleEventClick = (arg) => {
    const event = arg.event;
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      className: event.classNames[0],
      location: event.extendedProps.location,
      description: event.extendedProps.description,
      status: event.extendedProps.status,
      event_type: Array.isArray(event.extendedProps.event_type) ? event.extendedProps.event_type : [event.extendedProps.event_type],
      customer_id: event.extendedProps.customer_ids || [],
      service_id: event.extendedProps.service_ids || [],
      product_id: event.extendedProps.product_ids || [],
      invoice_id: event.extendedProps.invoice_ids || [],
    });
    setIsEditModalOpen(true);
  };

  const handleEventDrop = useCallback(async (info) => {
    const { event } = info;
    try {
      await pb.collection('events').update(event.id, {
        start_date: event.start.toISOString(),
        end_date: event.end?.toISOString() || event.start.toISOString(),
      });
      setRefreshKey(prev => prev + 1); // Verileri yenile
    } catch (error) {
      console.error("Error updating event:", error);
      info.revert();
    }
  }, [pb]);

  const handleEventReceive = useCallback(async (info) => {
    const { event } = info;
    try {
      const newEvent = {
        title: event.title,
        event_type: [event.extendedProps.event_type],
        start_date: event.start.toISOString(),
        end_date: event.end ? event.end.toISOString() : event.start.toISOString(),
      };
      await pb.collection('events').create(newEvent);
      setRefreshKey(prev => prev + 1); // Verileri yenile
    } catch (error) {
      console.error("Error creating event:", error);
      info.revert();
    }
  }, [pb]);

  const handleEventAdded = () => {
    setRefreshKey(prev => prev + 1); // Yeni event eklendiğinde verileri yenile
  };

  const handleEventUpdated = () => {
    setRefreshKey(prev => prev + 1); // Event güncellendiğinde verileri yenile
  };

  return (
    <div className="page-content">
      <style>
        {`
          .fc-event {
            padding: 2px 5px !important;
          }
          .fc-event-title, .fc-event-time {
            color: inherit !important;
            font-weight: 500;
          }
        `}
      </style>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Row>
              <Col xl={3}>
                <DraggableEvents toggleModal={() => setIsCreateModalOpen(true)} />
                <UpcomingEvents events={upcomingEvents} />
                <Card>
                  <CardBody className="bg-info-subtle">
                    <div className="d-flex">
                      <div className="flex-grow-1 ms-3">
                        <h6 className="fs-15">Takviminize Hoş Geldiniz!</h6>
                        <p className="text-muted mb-0">
                          Planlanan etkinlikler burada görünecektir. Detayları görmek ve yönetmek için bir etkinliğe tıklayın.
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={9}>
                <Card className="card-h-100">
                  <CardBody>
                    <FullCalendar
                      plugins={[
                        BootstrapTheme,
                        dayGridPlugin,
                        interactionPlugin,
                        listPlugin,
                        multiMonthPlugin
                      ]}
                      initialView="multiMonthYear"
                      slotDuration={"00:15:00"}
                      handleWindowResize={true}
                      themeSystem="bootstrap"
                      locale="tr"
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "multiMonthYear,dayGridMonth,dayGridWeek,dayGridDay,listWeek"
                      }}
                      multiMonthMaxColumns={3} // Sütun sayısı
                      multiMonthMinWidth={350} // Minimum genişlik
                      events={events}
                      editable={true}
                      droppable={true}
                      selectable={true}
                      dateClick={handleDateClick}
                      eventClick={handleEventClick}
                      eventDrop={handleEventDrop}
                      eventReceive={handleEventReceive}
                      eventClassNames={(arg) => [getEventClass(arg.event.extendedProps.event_type)]}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <CreateEventModal
              isOpen={isCreateModalOpen}
              toggle={() => setIsCreateModalOpen(!isCreateModalOpen)}
              selectedEvent={selectedEvent}
              onEventAdded={handleEventAdded}
              pb={pb}
            />
            <EditEventModal
              isOpen={isEditModalOpen}
              toggle={() => setIsEditModalOpen(!isEditModalOpen)}
              selectedEvent={selectedEvent}
              onEventUpdated={handleEventUpdated}
              pb={pb}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MonthGrid;